import React, { useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  Icons as GridIcons,
} from "Components/AppGrid/shipeezi-new-grid";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { Badge } from "reactstrap";

import Button, { Color, Colors } from "../../../../../../components/Form/Button";
import ChangeStatusModal from "../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../context/StateContext";
import { Error } from "../../../../../../models/error";
import { ServiceBooking as Material } from "../../../../../../models/service.booking";
import { Filters, Searches, Sorts, Sort, Row } from "../../../../../../models/shipeezi.table";
import MaterialsService from "../../../../../../services/materials.service";
import { handleGetConvertValue } from "../../../../../../util/converts";
import { Status } from "../../../../../../util/enums";
import { getMeasurement } from "../../../../../../util/getMeasurement";
import { onSaveItemSelected } from "../../../../../../util/onSaveItemSelected";
import MaterialModal from "./MaterialModal";
import useStyles from "./styles";
import { isShowAction } from "Util/permissions";

type Props = {
  permission?: {
    edit: boolean;
    create: boolean;
    changeStatus: boolean;
  };
};

const MaterialPage = (props: Props) => {
  const { permission } = props;
  const classes = useStyles();
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const [materials, setMaterials] = useState<Material[]>([]);
  const [selectedMaterial, setSelectedMaterial] = useState<Material>();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>();

  const [rows, setRows] = useState<Row[]>([]);
  const [clearSelection, setClearSelection] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Sorts>();

  const badgeStatus = (status: string) => (
    <Badge
      className={`badgeComponent ${classes.statusBadge} ${
        status === Status.ACTIVE ? classes.statusBadgeActive : ""
      }`}
    >
      {status}
    </Badge>
  );

  const { refetch, isFetching } = useQuery(
    ["materials.list", page, pageSize, filters, searches, sorting],
    () =>
      MaterialsService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        include: JSON.stringify([{ association: "rooms", attributes: ["id", "name"] }]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setMaterials(response.data);
      },
    }
  );

  const { refetch: getMaterialsTotal } = useQuery(
    ["materials.total", filters, searches],
    () =>
      MaterialsService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
        else setTotal(0);
      },
    }
  );

  const { mutate: updateMaterial, isLoading: isUpdating } = useMutation(
    ["material.update"],
    ({ id, material }: { id: number; material: Material }) => MaterialsService.update(id, material),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"), {
          id: "request.success.update.material ",
        });
        setHasError(false);
        setRows([]);
        setClearSelection(false);
        refetch();
        getMaterialsTotal();
      },
      onError: (err: AxiosError<Error>) => {
        if (err && err.response) NotificationToast.error(err?.response?.data?.message);
        setHasError(true);
      },
    }
  );

  const { mutate: createMaterial, isLoading: isCreating } = useMutation(
    ["material.create"],
    (material: Material) => MaterialsService.create(material),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        setHasError(false);
        refetch();
        getMaterialsTotal();
      },
      onError: (err: AxiosError<Error>) => {
        if (err && err.response) NotificationToast.error(err?.response?.data?.message);
        setHasError(true);
      },
    }
  );

  const editMaterial = (index: number) => {
    if (hasError !== undefined) setHasError(undefined);
    const findMaterial = materials.find((material) => Number(material.id) === Number(index));

    if (findMaterial) {
      setSelectedMaterial(findMaterial);
      setOpenModal(true);
    }
  };

  const changeMaterialStatus = (index: number) => {
    const findMaterial = materials.find((material) => Number(material.id) === Number(index));

    if (findMaterial) {
      updateMaterial({
        id: Number(index),
        material: {
          ...findMaterial,
          status: findMaterial.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        },
      });
    }
  };

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  const renderButtonToChangeStatus = () => {
    if (rows.length > 0) {
      return (
        <Button
          tooltip={translate("changeStatus")}
          className={classes.changeButtonStatus}
          customColor={Color.INFO}
          data-cy="changeStatus"
          onClick={() => {
            if (hasError !== undefined && !hasError) setHasError(undefined);
            setOpenConfirmation(true);
          }}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </Button>
      );
    }
  };

  const handleSaveMaterialSelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, materials);

    setRows([...rowsSelected]);
  };

  const columns: Column[] = [
    { name: "code", title: translate("code") },
    { name: "name", title: translate("name") },
    {
      name: "volume",
      title: translate("volume"),
      hideSearch: true,
      formatText: (volume: number) =>
        `${handleGetConvertValue(
          volume,
          getMeasurement()?.code,
          getMeasurement()?.decimalPlaces
        )} ${getMeasurement()?.code}`,
    },
    {
      name: "status",
      hideSearch: true,
      title: translate("status"),
      formatComponent: (status: string) => badgeStatus(status),
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus", "handleNewRecord"],
    },
    handleNewRecord:
      permission && isShowAction(currentUser, permission.create)
        ? {
            text: translate("create"),
            action: () => {
              if (hasError !== undefined) setHasError(undefined);
              setSelectedMaterial(undefined);
              setOpenModal(true);
            },
            tooltip: translate("create"),
          }
        : undefined,
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        { name: translate("code"), column: "code" },
        { name: translate("name"), column: "name" },
        { name: translate("volume"), column: "volume" },
        {
          name: translate("status"),
          column: "status",
          options: [Status.ACTIVE, Status.INACTIVE],
        },
      ],
      onChange: handleFilter,
    },
    handleChangeStatus:
      permission && isShowAction(currentUser, permission.changeStatus)
        ? renderButtonToChangeStatus()
        : undefined,
  };

  return (
    <div data-cy="materialList">
      <ShipeeziNewGrid
        data={materials}
        columns={columns}
        totalItems={total}
        loadingData={isFetching || isUpdating || isCreating}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        rowActions={{
          orderBy: ["handleEdit", "handleChange"],
          handleEdit:
            permission && isShowAction(currentUser, permission.edit)
              ? (materialId) => editMaterial(Number(materialId))
              : undefined,
          handleChange:
            permission && isShowAction(currentUser, permission.changeStatus)
              ? (materialId) => changeMaterialStatus(Number(materialId))
              : undefined,
        }}
        clearSelection={clearSelection}
        handleSelect={(rowsArr: number[]) => handleSaveMaterialSelected(rowsArr)}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
      />
      {openModal && (
        <MaterialModal
          open={openModal}
          material={selectedMaterial}
          hasError={hasError}
          isLoading={isUpdating || isCreating}
          closeModal={() => setOpenModal(false)}
          submit={(material: Material) => {
            if (material.id) updateMaterial({ id: material.id, material });
            else
              createMaterial({
                ...material,
                companyId: currentUser?.companyId,
              });
          }}
        />
      )}
      {openConfirmation && (
        <ChangeStatusModal
          open={openConfirmation}
          rows={rows}
          isLoading={isUpdating}
          hasErrors={hasError}
          closeModal={() => setOpenConfirmation(false)}
          update={(rowId, status) => {
            if (rowId && status) {
              updateMaterial({ id: rowId, material: { status } as Material });
              setClearSelection(true);
            }
          }}
        />
      )}
    </div>
  );
};

export default MaterialPage;
